import { TRACKING_INTEGRATION_STATUSES, type TrackingIntegration, type TrackingIntegrationListQuery } from '@chukua/common';
import { Button, Popconfirm, Tag, type TableColumnsType } from 'antd';
import type { ListQueryControls } from '../../../shared/hooks/use-list-query';
import { lastTestSummary, STATUS_LABELS, STATUS_TAG_COLORS } from '../policies/integration-status.policy';

interface BuildIntegrationColumnsArgs {
  list: Pick<ListQueryControls<TrackingIntegrationListQuery>, 'sortOrderFor' | 'filteredValueFor'>;
  adapterOptions: { text: string; value: string }[];
  onEnable: (integration: TrackingIntegration) => void;
  onDisable: (integration: TrackingIntegration) => void;
  onUninstall: (integration: TrackingIntegration) => void;
}

/** Sorting and filtering run on the server: each column's `key` is its sort field or filter param in `trackingIntegrationListQuerySchema`. */
export function buildIntegrationColumns({
  list,
  adapterOptions,
  onEnable,
  onDisable,
  onUninstall,
}: BuildIntegrationColumnsArgs): TableColumnsType<TrackingIntegration> {
  return [
    { title: 'Name', dataIndex: 'displayName', key: 'displayName', sorter: true, sortOrder: list.sortOrderFor('displayName') },
    { title: 'Adapter', dataIndex: 'adapterName', key: 'adapterKey', filters: adapterOptions, filteredValue: list.filteredValueFor('adapterKey') },
    {
      title: 'Status',
      key: 'status',
      filters: TRACKING_INTEGRATION_STATUSES.map((status) => ({ text: STATUS_LABELS[status], value: status })),
      filteredValue: list.filteredValueFor('status'),
      render: (_, integration) => <Tag color={STATUS_TAG_COLORS[integration.status]}>{STATUS_LABELS[integration.status]}</Tag>,
    },
    { title: 'Last test', key: 'lastTest', render: (_, integration) => <span className="text-muted">{lastTestSummary(integration)}</span> },
    {
      title: 'Polling',
      key: 'polling',
      render: (_, integration) => (integration.pollingIntervalSeconds ? `Every ${integration.pollingIntervalSeconds}s` : '—'),
    },
    {
      title: '',
      key: 'actions',
      render: (_, integration) => <RowActions integration={integration} onEnable={onEnable} onDisable={onDisable} onUninstall={onUninstall} />,
    },
  ];
}

interface RowActionsProps extends Pick<BuildIntegrationColumnsArgs, 'onEnable' | 'onDisable' | 'onUninstall'> {
  integration: TrackingIntegration;
}

/** Stops the click reaching the row, which opens the edit sheet. */
function RowActions({ integration, onEnable, onDisable, onUninstall }: RowActionsProps) {
  if (integration.status === 'uninstalled') return null;
  return (
    <span className="flex gap-1" onClick={(event) => event.stopPropagation()}>
      {integration.status === 'enabled' ? (
        <Button size="small" onClick={() => onDisable(integration)}>
          Disable
        </Button>
      ) : (
        <Button size="small" onClick={() => onEnable(integration)}>
          Enable
        </Button>
      )}
      <Popconfirm
        title={`Uninstall ${integration.displayName}?`}
        description="Its device mappings end now. Positions and events already received are kept."
        okText="Uninstall"
        okButtonProps={{ danger: true }}
        onConfirm={() => onUninstall(integration)}
      >
        <Button size="small" danger>
          Uninstall
        </Button>
      </Popconfirm>
    </span>
  );
}
